import React from "react";

/**
 * `React.lazy`, переживающий выкат новой версии.
 *
 * Что чинится. Вкладку склада не закрывают сутками, а деплой меняет хэши
 * чанков: старый файл с сервера исчезает и отдаёт честный 404 (nginx на
 * `/assets/*.js` не подставляет index.html — проверено). Ленивый импорт при
 * этом падает с «Importing a module script failed», и кладовщик видит белый
 * экран ровно в тот момент, когда нажал «Сканировать». Своей вины у него нет,
 * и подсказки «перезагрузите» на экране тоже нет.
 *
 * Перезагрузка здесь — не фоллбэк, маскирующий сбой, а единственное верное
 * разрешение ситуации «в браузере одна версия приложения, на сервере другая»:
 * после неё грузится свежий index.html (он отдаётся `no-store`) и актуальные
 * чанки.
 *
 * Цикл исключён меткой в `sessionStorage`: перезагружаемся ОДИН раз в минуту
 * на вкладку. Если чанк не загрузился и после этого — причина другая (нет
 * сети, сломанная сборка), и ошибка идёт наверх как есть, а не превращается
 * в бесконечно моргающую страницу.
 */
const RELOAD_KEY = "wh_chunk_reload_at";
const RELOAD_COOLDOWN_MS = 60_000;

function reloadOnce(): boolean {
  try {
    const prev = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - prev < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Приватный режим без sessionStorage: защиты от цикла нет, поэтому и
    // не перезагружаем — лучше показать ошибку, чем моргать страницей.
    return false;
  }
  window.location.reload();
  return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LazyChunk<T extends React.ComponentType<any>> =
  React.LazyExoticComponent<T> & {
    /** Подтянуть чанк заранее (см. `usePreloadChunk`). */
    preload: () => void;
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyChunk<T extends React.ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyChunk<T> {
  const guarded = () =>
    load().catch((err: unknown) => {
      if (reloadOnce()) {
        // Перезагрузка уже началась; отдаём никогда не разрешающийся промис,
        // чтобы React не успел показать заглушку ошибки перед уходом.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    });
  const Component = React.lazy(guarded) as LazyChunk<T>;
  Component.preload = () => {
    void guarded().catch(() => {
      /* ошибку покажет сам рендер, когда чанк действительно понадобится */
    });
  };
  return Component;
}

/**
 * Подтянуть чанк при ВХОДЕ на экран, а не при нажатии кнопки.
 *
 * Перезагрузка из-за устаревшего чанка неизбежна (в браузере одна версия, на
 * сервере другая), но момент выбирается нами. Без предзагрузки он приходился
 * на тап «Сканировать» — то есть на середину работы, когда человек уже
 * поднёс телефон к коробу. Теперь версия проверяется, когда экран только
 * открыли: перезагрузка происходит там, где она ничего не прерывает.
 */
export function usePreloadChunk(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...chunks: LazyChunk<any>[]
): void {
  React.useEffect(() => {
    for (const c of chunks) c.preload();
    // Список чанков у экрана постоянный — перезапускать эффект не на что.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
